import React, { useEffect, useState } from "react";
import axios from "axios";
import { baseUrl } from "../../../../helper/urls";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import Select from "react-select";

const CreateRequest = () => {
  const { token } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [counselorList, setCounselorList] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [selectedCounselor, setSelectedCounselor] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [timeValue, setTimeValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ time: false });

  const timeOptions = ["9AM-12PM", "12PM-3PM", "3PM-6PM", "6PM-9PM"];

  useEffect(() => {
    fetchCounselors();
    fetchStudents();
  }, []);

  const fetchCounselors = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/counselors/counselorlist`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCounselorList(response.data.data || []);
    } catch (err) {
      console.error("Failed to load counselors", err);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/students/list`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setStudentList(response.data.data || []);
    } catch (err) {
      console.error("Failed to load students", err);
    }
  };

  const createBookingRequest = async () => {
    setLoading(true);

    const startHourStr = timeValue?.split("-")[0];
    const localTime = new Date();

    if (startHourStr) {
      const hourMatch = startHourStr.match(/(\d+)(AM|PM)/);
      if (hourMatch) {
        let hour = parseInt(hourMatch[1]);
        const period = hourMatch[2];
        if (period === "PM" && hour !== 12) hour += 12;
        if (period === "AM" && hour === 12) hour = 0;
        localTime.setHours(hour, 0, 0, 0);
      }
    }

    const utcTime = localTime.toISOString();

    try {
      const response = await axios.post(
        `${baseUrl}/api/booking_req/adminCreate`,
        {
          counselorId: selectedCounselor,
          studentId: selectedStudent,
          call_time: timeValue,
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          utcTime: utcTime,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success === true) {
        Swal.fire({
          text: "Request created successfully",
          icon: "success",
          timer: 3000,
          showConfirmButton: true,
        }).finally(() => {
          navigate("/request");
        });
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to create booking request"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    const newErrors = {
      time: !timeValue,
      counselor: !selectedCounselor,
      student: !selectedStudent,
    };
    setErrors(newErrors);
    if (!newErrors.time && !newErrors.counselor && !newErrors.student) {
      createBookingRequest();
    }
  };

  const styles = {
    group: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      width: "100%",
    },
    label: {
      fontSize: "14px",
      fontWeight: 500,
      color: "#333",
    },
    selectWrapper: {
      position: "relative",
      minWidth: "180px",
    },
    select: {
      width: "100%",
      // padding: "6px 30px 6px 10px",
      fontSize: "14px",
      height: "36px",
      border: "1px solid #ccc",
      borderRadius: "6px",
      backgroundColor: "#fff",
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",
      cursor: "pointer",
    },
    arrow: {
      position: "absolute",
      top: "50%",
      right: "10px",
      pointerEvents: "none",
      transform: "translateY(-50%)",
      fontSize: "12px",
      color: "#555",
    },
    button: {
      padding: "8px 20px",
      backgroundColor: "#124178",
      color: "#fff",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      width: "100%",
      height: "37px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#124178" : "#ccc",
      boxShadow: state.isFocused ? "0 0 0 1px #124178" : "none",
      "&:hover": {
        borderColor: "#124178",
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#124178"
        : state.isFocused
        ? "#e6f0ff"
        : "#fff",
      color: state.isSelected ? "#fff" : "#000",
      cursor: "pointer",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#124178", // selected value text color
      fontWeight: 500,
    }),
  };

  return (
    <div
      style={{
        minHeight: "65vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ToastContainer position="top-right" autoClose={2000} />

      <div
        style={{
          background: "#fff",
          padding: "24px",
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          width: "80%",
          height: "20rem",
        }}
      >
        {/* Row 1: Counselor + Student */}
        <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
          <div style={styles.group}>
            <label style={styles.label}>Select Counselor</label>
            <Select
              styles={customSelectStyles}
              options={counselorList.map((c) => ({
                value: c.id,
                label: `${c.firstName} ${c.lastName} (${c.email})`,
              }))}
              value={
                counselorList
                  .map((c) => ({
                    value: c.id,
                    label: `${c.firstName} ${c.lastName} (${c.email})`,
                  }))
                  .find((option) => option.value === selectedCounselor) || null
              }
              onChange={(selectedOption) =>
                setSelectedCounselor(selectedOption?.value || "")
              }
              placeholder="Search counselor..."
              isClearable
            />
          </div>

          <div style={styles.group}>
            <label style={styles.label}>Select Student</label>
            <Select
              styles={customSelectStyles}
              options={studentList.map((s) => ({
                value: s.id,
                label: `${s.firstName} ${s.lastName} (${s.email})`,
              }))}
              value={
                studentList
                  .map((s) => ({
                    value: s.id,
                    label: `${s.firstName} ${s.lastName} (${s.email})`,
                  }))
                  .find((option) => option.value === selectedStudent) || null
              }
              onChange={(selectedOption) =>
                setSelectedStudent(selectedOption?.value || "")
              }
              placeholder="Search student..."
              isClearable
            />
          </div>
        </div>

        {/* Row 2: Time + Button */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={styles.group}>
            <label style={styles.label}>Select Time</label>
            <Select
              styles={customSelectStyles}
              options={timeOptions.map((t) => ({ value: t, label: t }))}
              value={
                timeOptions
                  .map((t) => ({ value: t, label: t }))
                  .find((option) => option.value === timeValue) || null
              }
              onChange={(selectedOption) =>
                setTimeValue(selectedOption?.value || "")
              }
              placeholder="Select time"
              isClearable
              isSearchable={false} // 🔒 disables search
            />
          </div>
        </div>
        {/* Row 3: Time + Button */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "2rem",
          }}
        >
          <div style={{ width: "100%", marginTop: "2rem" }}>
            <button
              style={styles.button}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateRequest;
